import type { Cents } from "./money";
import type { CompanyFundingSourcePolicies } from "./fundingProject";

/** Planning inputs only. None of these records are cash, debt, expense or journal authority. */
export type FundingPermissionStatus = "unknown" | "allowed" | "prohibited";
export type FundingPurpose = "owner-distribution" | "gc-compensation" | "project-cost" | "cross-project";
export interface FundingPermission {
  purpose: FundingPurpose;
  status: FundingPermissionStatus;
  projectId: string | null;
  evidenceReference: string | null;
  confirmedOn: string | null;
}
export interface FacilityFundingPermissions {
  facilityId: string;
  permissions: FundingPermission[];
  availableFrom: string | null;
  availableThrough: string | null;
  /** Unknown contractual timing remains unknown; never assume immediate reimbursement. */
  reimbursementDelayDays: number | null;
  /** Explicit review of incremental fees, accrual, repayment and payoff terms. Missing means unconfirmed. */
  materialTermsConfirmed?: boolean;
  confirmedPayoffDate?: string | null;
  /** Company-wide use must be explicitly permitted; owner distributions alone do not imply it. */
  unrestrictedCompanyUseConfirmed?: boolean;
}
export interface CashRestriction {
  id: string;
  amountCents: Cents;
  projectId: string | null;
  facilityId: string | null;
  purpose: string;
  /** Explicit provenance for already-funded GC cash, never inferred from purpose text. */
  gcBindingId?: string | null;
}
export interface PlanningCashAnchor {
  id: string;
  accountId: string;
  asOfDate: string;
  balanceCents: Cents | null;
  evidence: "reconciled-statement" | "statement" | "manual" | "unknown";
  evidenceReference: string | null;
  confirmed: boolean;
  restrictions: CashRestriction[];
}
export interface PersonalCashTarget {
  monthlyCents: Cents | null;
  startDate: string;
  durationMonths: number;
  /** Personal tax cash is in addition to spendable target; null explicitly unresolved. */
  personalTaxReserveMonthlyCents: Cents | null;
  /** Absent on legacy v1 plans means the original anniversary-period-start timing. New plans default weekly. */
  timing?: "weekly" | "beginning-of-month" | "custom";
  /** Explicit dated personal needs replace the monthly template in custom mode. */
  customRequirements?: Array<{ id: string; date: string; spendableCents: Cents | null; taxReserveCents: Cents | null }>;
}
export interface PersonalCashReserve {
  asOfDate: string;
  balanceCents: Cents | null;
  confirmed: boolean;
  evidenceReference: string | null;
}
export interface SupplementalPersonalIncome {
  id: string;
  name: string;
  cadence: "monthly" | "weekly" | "dated";
  startDate: string;
  endDate: string | null;
  expectedCents: Cents;
  conservativeCents: Cents | null;
  /** Personal resources offset the target only, never company bank cash or project revenue. */
  scope: "personal-only";
}
export interface FundingReservePolicy {
  minimumUnrestrictedCents: Cents | null;
  /** Display suggestion only. Not a balance and not automatically an accepted reserve. */
  suggestedReserveMonths: number;
  constructionContingencyPctX100: number;
  contingencyPolicySource: "suggested" | "confirmed";
  projectOverrides: Array<{ projectId: string; contingencyPctX100: number }>;
}
export interface FundingAcquisitionTarget {
  id: string;
  scenarioProjectId: string | null;
  name: string;
  anticipatedDate: string;
  purchasePriceCents: Cents | null;
  downPaymentPctX100: number | null;
  closingCostsCents: Cents | null;
  requiredReservesCents: Cents | null;
}
export interface FundingPreferences {
  strategy: "lowest-interest" | "highest-liquidity" | "balanced";
  fundingCadence: "automatic" | "weekly" | "monthly";
  /** Opportunity dates are derived from sources; this is a preference, not a fabricated draw. */
  accumulateBeforeConstructionEnds: boolean;
  preferredFundingThrough: string | null;
  hardBorrowingCutoff: string | null;
}
export interface CompanyFundingScenario {
  version: 1;
  planningOnly: true;
  id: string;
  name: string;
  asOfDate: string;
  target: PersonalCashTarget;
  cashAnchors: PlanningCashAnchor[];
  facilityPermissions: FacilityFundingPermissions[];
  reservePolicy: FundingReservePolicy;
  supplementalIncome: SupplementalPersonalIncome[];
  acquisitions: FundingAcquisitionTarget[];
  preferences: FundingPreferences;
  /** Explicit input-coverage review, never authority to waive individual unknowns/restrictions. */
  baselineCoverageConfirmed?: boolean;
  /** Explicit personal balance permits dated bridging/carry; never company cash. */
  personalCashReserve?: PersonalCashReserve;
  sourcePolicies?: CompanyFundingSourcePolicies;
}

/** No verified cash or lending permission is supplied by defaults. */
export function createCompanyFundingScenario(id: string, asOfDate: string): CompanyFundingScenario {
  return {
    version: 1, planningOnly: true, id, name: "Owner cash plan", asOfDate,
    target: { monthlyCents: null, startDate: asOfDate, durationMonths: 8, personalTaxReserveMonthlyCents: null, timing: "weekly" },
    cashAnchors: [], facilityPermissions: [], supplementalIncome: [], acquisitions: [],
    reservePolicy: { minimumUnrestrictedCents: null, suggestedReserveMonths: 1, constructionContingencyPctX100: 1000, contingencyPolicySource: "suggested", projectOverrides: [] },
    preferences: { strategy: "balanced", fundingCadence: "automatic", accumulateBeforeConstructionEnds: false, preferredFundingThrough: null, hardBorrowingCutoff: null },
  };
}
